import { useEffect, useState } from 'react';

/**
 * Options for configuring the useElapsedTime hook behavior
 */
interface UseElapsedTimeOptions {
  /** The timestamp (in milliseconds) when timing should start from */
  startTime: number;
  /** Whether the timer is currently active and should update */
  enabled?: boolean;
  /** Whether to reset the duration to 0 when the timer is disabled */
  resetOnDisable?: boolean;
}

/**
 * A React hook that tracks elapsed time from a given start timestamp
 *
 * @param options - Configuration options for the elapsed time tracker
 * @param options.startTime - The timestamp (in milliseconds) when timing should start from
 * @param options.enabled - Whether the timer is currently active and should update (defaults to true)
 * @param options.resetOnDisable - Whether to reset the duration to 0 when the timer is disabled (defaults to false)
 *
 * @returns The elapsed time in milliseconds since the start time
 *
 * @example
 * ```typescript
 * // Basic usage - track time since a call started
 * const callStartTime = 1692000000000;
 * const elapsedTime = useElapsedTime({ startTime: callStartTime });
 *
 * // With enable/disable control
 * const [isActive, setIsActive] = useState(true);
 * const elapsedTime = useElapsedTime({
 *   startTime: callStartTime,
 *   enabled: isActive
 * });
 *
 * // Reset to 0 when disabled
 * const elapsedTime = useElapsedTime({
 *   startTime: callStartTime,
 *   enabled: isActive,
 *   resetOnDisable: true
 * });
 * ```
 */
interface UseElapsedTimeOptions {
  startTime: number;
  enabled?: boolean;
  resetOnDisable?: boolean;
}

export function useElapsedTime({
  startTime,
  enabled = true,
  resetOnDisable = false,
}: UseElapsedTimeOptions) {
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    if (startTime > 0 && enabled) {
      // Set initial duration immediately when enabled
      setDuration(Date.now() - startTime);

      const interval = setInterval(() => {
        setDuration(Date.now() - startTime);
      }, 1000);

      return () => clearInterval(interval);
    } else if (!enabled && resetOnDisable) {
      // Only reset duration when disabled if resetOnDisable is true
      setDuration(0);
    }
  }, [startTime, enabled, resetOnDisable]);

  return duration;
}
